import { DeepPartial } from 'redux';
import { variant, VariantArgs } from 'styled-system';
import styled, { css, ThemedStyledFunction } from 'styled-components';

import { WithTheme } from 'config/theme';

import { globalColors, standardLighterShadow } from 'components/AppLayout/config';
import { ButtonProps } from './Button.types';

export const buttonBoxShadowVoffset = 4;

const lightBgAndAccentedColor = {
    bg: 'accent.light',
    color: 'font.onAccent'
};

const lightWithoutHoverAndFocus = {
    color: 'accent.dark',
    bg: 'secondary',
    '&:active': {
        bg: 'accent.light',
        color: 'font.onAccent'
    },
    '&:disabled, &[disabled]': { color: 'font.onPrimaryLighter' }
};

const lightWithoutFocus = {
    ...lightWithoutHoverAndFocus,
    ...{ '&:hover': lightBgAndAccentedColor }
};

const light = {
    ...lightWithoutHoverAndFocus,
    ...{ '&:hover, &:focus': lightBgAndAccentedColor }
};

const variantArgs: VariantArgs & { variants: Record<string, DeepPartial<typeof light>> } = {

    variants: {
        white: {
            color: 'accent.dark',
            bg: 'white',
            '&:hover, &:focus': { bg: 'secondary' },
            '&:active': {
                bg: 'secondary',
                color: 'accent.dark'
            },
            '&:disabled, &[disabled]': { color: 'font.onPrimaryLighter' }
        },
        light,
        lightWithoutFocus,
        lightWithoutHoverAndFocus,
        accent: {
            color: globalColors.fontColors.green.base,
            bg: 'accent.light',
            '&:hover, &:focus': {
                bg: 'accent.base',
                color: globalColors.fontColors.white
            },
            '&:active': {
                bg: 'accent.base',
                color: globalColors.fontColors.white
            },
            '&:disabled, &[disabled]': {
                color: 'font.onDark',
                bg: 'gray.xdark'
            }
        },
        error: {
            color: 'font.onAccent',
            bg: 'error.base',
            '&:hover, &:focus': {
                bg: 'error.darker',
                color: 'font.onAccent'
            },
            '&:active': {
                bg: 'error.darker',
                color: 'font.onAccent'
            },
            '&:disabled, &[disabled]': {
                color: 'font.onDark',
                bg: 'gray.xdark'
            }
        },
        warn: {
            color: 'font.onAccent',
            bg: 'warn.base',
            '&:hover, &:focus': {
                bg: 'warn.darker',
                color: 'font.onAccent'
            },
            '&:active': {
                bg: 'warn.darker',
                color: 'font.onAccent'
            },
            '&:disabled, &[disabled]': {
                color: 'font.onDark',
                bg: 'gray.xdark'
            }
        },

        info: {
            color: 'font.onAccent',
            bg: 'info.base',
            '&:hover, &:focus': {
                bg: 'info.darker',
                color: 'font.onAccent'
            },
            '&:active': {
                bg: 'info.darker',
                color: 'font.onAccent'
            },
            '&:disabled, &[disabled]': {
                color: 'font.onDark',
                bg: 'gray.xdark'
            }
        },
    }
};

const buttonBaseStyle = ({ theme }: Partial<WithTheme>) => css`
    ${standardLighterShadow}
    border: 1px solid transparent;
    cursor: pointer;
    text-align: center;
    font-size: ${theme?.fontSizes[1] || '1rem'};
    font-weight: ${theme?.fontWeights.semiBold || '600'};
`;

const createStyledButton: ThemedStyledFunction<'button', WithTheme & ButtonProps> = styled('button');

export const ButtonBaseWithVariants = createStyledButton(buttonBaseStyle, variant(variantArgs));

ButtonBaseWithVariants.defaultProps = {
    variant: 'accent',
    width: 'auto'
};
